import { UnitSystem } from "components/UserPreferences/UserPreferencesContext"
import { AggregationInterval } from "gen/graphql"
import { DateTime, DurationLikeObject, Interval } from "luxon"

export function formatMonthYear(year: number, month: number, day?: number): string {
  let dateString = `${year}-${String(month).padStart(2, "0")}`
  if (day) {
    dateString += String(day).padStart(2, "0")
  }
  return dateString
}

export function formatYearWeek(value: string): string {
  const [year, week] = value.split("-")
  const date = new Date(+year, 0, +week * 7)
  const firstDayOfWeek = getFirstDayOfWeek(date)
  const lastDayOfWeek = new Date(firstDayOfWeek)
  lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6)

  return `${DateTime.fromJSDate(firstDayOfWeek).toFormat("dd/MM")}–${DateTime.fromJSDate(
    lastDayOfWeek,
  ).toFormat("dd/MM")}`
}

export function toDateTime(year: number, month: number, day?: number): DateTime {
  return DateTime.fromISO(formatMonthYear(year, month, day))
}

function getFirstDayOfWeek(d: Date): Date {
  const date = new Date(d)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)

  return new Date(date.setDate(diff))
}

export const getPreviousPeriodStartTime = (startTime: DateTime, endTime: DateTime): DateTime => {
  const diff = Interval.fromDateTimes(startTime, endTime)
  return startTime.minus({ days: diff.count("days") })
}

export const formatDate = (
  date: DateTime,
  aggregationInterval: AggregationInterval,
  unitSystem: UnitSystem,
): string => {
  switch (aggregationInterval) {
    case AggregationInterval.Day:
      return unitSystem === UnitSystem.Metric ? date.toFormat("d LLL") : date.toFormat("LLL d")
    case AggregationInterval.Week:
      return unitSystem === UnitSystem.Metric ? date.toFormat("d LLL") : date.toFormat("LLL d")
    case AggregationInterval.Month:
      return date.toFormat("LLL `yy")
    case AggregationInterval.Year:
      return date.toFormat("yyyy")
    default:
      return date.toFormat("dd")
  }
}

export const toDateTimeInterval = (
  aggregationInterval: AggregationInterval,
): keyof DurationLikeObject => {
  switch (aggregationInterval) {
    case AggregationInterval.Day:
      return "days"
    case AggregationInterval.Week:
      return "weeks"
    case AggregationInterval.Month:
      return "months"
    case AggregationInterval.Year:
      return "years"
    default:
      return "days"
  }
}

export function formatCustomRange(startDate: DateTime, endDate: DateTime): string {
  if (startDate.hasSame(endDate, "year")) {
    return `${startDate.toFormat("LLL dd")} - ${endDate.toFormat("LLL dd, yyyy")}`
  }
  return `${startDate.toFormat("LLL dd, YYYY")} - ${endDate.toFormat("LLL dd, yyyy")}`
}
